
export const normalizeSemanticGapItem = (item) => {
  if (!item) return null;

  if (typeof item === 'string') {
    const trimmed = item.trim();
    if (!trimmed) return null;

    const match = trimmed.match(/^(.+?)\((\d+)\)$/);
    if (match) {
      return {
        word: match[1].trim(),
        frequency: parseInt(match[2], 10),
        insideKeywords: [],
      };
    }

    return { word: trimmed, frequency: 1, insideKeywords: [] };
  }

  const word = (item.word || item.name || item.keyword || '').toString().trim();
  if (!word) return null;

  const rawFrequency = item.frequency ?? item.count ?? item.freq ?? item.total ?? 1;
  const frequency = Number(rawFrequency);

  return {
    word,
    frequency: Number.isFinite(frequency) && frequency > 0 ? frequency : 1,
    insideKeywords: Array.isArray(item.insideKeywords) ? item.insideKeywords : [],
  };
};


export const normalizeSemanticGapList = (data) => {
  if (!Array.isArray(data)) return [];
  return data.map(normalizeSemanticGapItem).filter(Boolean);
};


export const buildGapFromClientFreqV2 = (semanticData) => {
  const clientFreqV2 = Array.isArray(semanticData?.clientFreqV2)
    ? semanticData.clientFreqV2
    : Array.isArray(semanticData)
      ? semanticData
      : [];

  const result = [];
  const seen = new Set();

  clientFreqV2.forEach((item) => {
    const word = (item?.name || item?.word || item?.keyword || '')
      .toString()
      .trim();
    if (!word) return;

    const key = word.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);

    const rawFrequency = Number(item?.total ?? item?.count ?? item?.frequency ?? 1);
    result.push({
      word,
      frequency: Number.isFinite(rawFrequency) && rawFrequency > 0 ? rawFrequency : 1,
      insideKeywords: [],
    });
  });

  return result;
};

/**
 * Formats an array of keyword data into a string.
 * Each keyword item contains a main keyword with a frequency and a list of inside keywords.
 * The format for each item is:
 * - If there are inside keywords: "mainKeyword(frequency),\ninsideKeyword1, insideKeyword2, ..."
 * - If there are no inside keywords: "mainKeyword(frequency)"
 * All formatted entries are joined together with ',\n'.
 *
 * @param {Array} data - Array of keyword objects with structure:
 *   { word: string, frequency: number, insideKeywords: Array<{ keyword: string }> }
 * @returns {string} - Formatted string representation of the keyword data.
 */

export const formatKeywordData = (data) => {
  return normalizeSemanticGapList(data)
    .map((item) => {
      const mainKeywordFormatted = `${item.word}(${item.frequency})`;
      const insideKeywordsFormatted = (item.insideKeywords || [])
        .map((ik) => (typeof ik === 'string' ? ik : ik?.keyword))
        .filter(Boolean)
        .join('\n');

      return insideKeywordsFormatted
        ? `${mainKeywordFormatted},\n${insideKeywordsFormatted}`
        : mainKeywordFormatted;
    })
    .join(',\n');
};

export const formatwebsiteUrlsToArr = (data) => {
  if (!data) return [];

  const arr = data.split(/\s*,?\s*\n\s*/g);

  const cleanData = arr.map((word) => word.replace(/^new page-/, '').trim());

  return cleanData;
};

export const formatwebsiteUrls = (data) => {
  return formatwebsiteUrlsToArr(data).join(',\n');
};

export const extractKeywordsWithFrequency = (semanticGap) => {
  if (typeof semanticGap === 'string') {
    return extractStrKeywordsWithFrequency(semanticGap);
  }


  const keywordsWithFrequency = normalizeSemanticGapList(semanticGap).map((item) => {
    return `${item.word}(${item.frequency})`;
  });

  // Join the formatted keywords into a single string separated by commas
  return keywordsWithFrequency.join(',\n');
};

/**
 * Extracts and returns only the keywords that follow the correct format:
 * "keyword (frequency)" where the frequency is a numeric value in parentheses.
 *
 * It ignores entries that:
 * - Are missing parentheses
 * - Contain non-numeric values inside parentheses
 * - Are not properly formatted
 *
 * The input string can have keywords separated by new lines or commas.
 * The result is a comma-separated string of valid keyword-frequency pairs.
 *
 * Example:
 * --------
 * Input:
 * `
 *   apple (5)
 *   banana
 *   orange (two)
 *   grape (3), lemon (8)
 * `
 *
 * Output:
 * "apple (5), grape (3), lemon (8)"
 *
 * @param {string} input - Raw string containing keywords, optionally with frequencies.
 * @returns {string} - A cleaned, comma-separated string of valid keyword-frequency pairs.
 */

export const extractStrKeywordsWithFrequency = (input) => {
  // Split the input by new lines or commas, and trim whitespace
  const parts = input.split(/[\n,]+/).map((part) => part.trim());

  // Filter the parts that follow the pattern: multi-word or single word + (digits)
  const matches = parts.filter((part) => {
    // Find the opening and closing parentheses
    const openParen = part.lastIndexOf('('); // Use lastIndexOf in case there are parentheses in the text
    const closeParen = part.indexOf(')');

    // Check if the format matches: any text + (digits)
    if (openParen > 0 && closeParen === part.length - 1) {
      const keyword = part.slice(0, openParen).trim(); // The keyword is the text before the parentheses
      const frequency = part.slice(openParen + 1, closeParen);

      // Validate if the frequency is numeric
      return keyword.length > 0 && /^\d+$/.test(frequency);
    }

    return false;
  });

  // Join the valid matches back into a string, separated by commas
  return matches.join(', ');
};

// Function to transform API response
export const transformData = (apiResponse) => {
  if (!apiResponse || !apiResponse.result) {
    console.warn('API response is undefined or missing result:', apiResponse);
    return [];
  }

  return apiResponse.result.map((item) => ({
    frequency: item.freq,
    insideKeywords: item.insideKeyword.map((ik) => ({ keyword: ik.keyword })),
    word: item.keyword,
  }));
};

/**
 * Transforms a structured string of keywords into a semantic gap format.
 *
 * Each main keyword is expected to be on its own line in the format:
 *   keyword(frequency)
 *
 * The lines that follow (until the next main keyword) are considered
 * "inside keywords" (related subtopics) that don't contain frequencies.
 *
 * The result is an array of objects, where each object represents:
 * - `word`: the main keyword
 * - `frequency`: the numeric frequency value
 * - `insideKeywords`: an array of related keywords (strings only)
 *
 * Example:
 * --------
 * Input:
 * `
 *   code(2)
 *   javascript
 *   react
 *   test(1)
 *   mocha
 * `
 *
 * Output:
 * [
 *   {
 *     word: "code",
 *     frequency: 2,
 *     insideKeywords: [
 *       { keyword: "javascript" },
 *       { keyword: "react" }
 *     ]
 *   },
 *   {
 *     word: "test",
 *     frequency: 1,
 *     insideKeywords: [
 *       { keyword: "mocha" }
 *     ]
 *   }
 * ]
 *
 * @param {string} keywords - A string with keywords and optional inside keywords.
 * @returns {Array} - Parsed array of keyword objects in semantic gap format.
 */
export const transformToSemanticGap = (keywords) => {
  // Split the keywords string into individual lines
  const lines = keywords.split(/,\n|\n/);

  // Initialize the semanticGap array
  const semanticGap = [];

  // Iterate through the lines to parse keywords and inside keywords
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Match main keywords with frequency, e.g., "code(2)"
    const mainKeywordMatch = line.match(/^(.+?)\((\d+)\)$/);

    if (mainKeywordMatch) {
      // Extract word and frequency
      const word = mainKeywordMatch[1].trim();
      const frequency = parseInt(mainKeywordMatch[2], 10);

      // Initialize the insideKeywords array
      const insideKeywords = [];

      // Check for subsequent inside keywords (not containing frequency)
      while (i + 1 < lines.length && !lines[i + 1].includes('(')) {
        i++;
        const insideKeyword = lines[i].trim();
        insideKeywords.push({ keyword: insideKeyword });
      }

      // Add the parsed data to semanticGap
      semanticGap.push({ word, frequency, insideKeywords });
    }
  }

  return semanticGap;
};

/**
 * Transforms a keyword string with frequencies into a single object:
 * {
 *   keyword1: frequency1,
 *   keyword2: frequency2,
 *   ...
 * }
 *
 * Ignores any lines that do not match the format "keyword(frequency)".
 *
 * Example input:
 * `
 * électrique(3)
 * chauffage(2)
 * avis client(1)
 * `
 *
 * Output:
 * {
 *   "électrique": 3,
 *   "chauffage": 2,
 *   "avis client": 1
 * }
 *
 * @param {string} keywords - A string containing keywords with frequency in parentheses
 * @returns {Object} - An object mapping each keyword to its frequency
 */
export const transformToKeywordFrequencyMap = (keywords) => {
  const lines = keywords.split(/,\n|\n/);
  const result = {};

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (!line) continue;

    const match = line.match(/^(.+?)\((\d+)\)$/);
    if (match) {
      result[match[1].trim()] = parseInt(match[2], 10);
    } else {
      result[line] = 1;
    }
  }

  return result;
};
